// Auditoria do 1º dia útil (#5791) — o que cada Secretaria deve a mais no ciclo do mês.
//
// O contrato manda emitir até o último dia útil e auditar no primeiro dia útil seguinte. Auditar é
// olhar cinco competências e dizer o que ficou para trás: Secretaria não emitida, UC que ficou fora
// do fechamento, boleto sem linha digitável, NF ausente, malote não entregue, pagamento a menor.
//
// Tudo aqui é leitura, sem I/O: as datas vêm da régua (reguaPrazosAgrupamento.ts) e os valores do
// fechamento. O registro da auditoria é por Secretaria, no mesmo grão do resto do módulo, e o ciclo
// é considerado auditado quando as cinco têm carimbo — nada guardado em separado.

import type { SituacaoPagamento } from './pagamentoRetencaoAgrupamento';
import type { EstadoPrazoEmissao } from './reguaPrazosAgrupamento';

/** Gravidade da pendência: o que impede fechar o ciclo e o que só precisa de olho. */
export type GravidadePendencia = 'impedimento' | 'atencao';

export interface Pendencia {
  codigo: string;
  texto: string;
  gravidade: GravidadePendencia;
}

/** O fechamento do mês de uma Secretaria, com o que a auditoria precisa olhar. */
export interface FechamentoAuditoria {
  id: number;
  agrupamentoId: number;
  nome: string;
  status: 'aberto' | 'fechado' | 'emitido' | string;
  totalVigor: number | null;
  valorCobrado: number | null;
  ucsNoGrupo: number | null;
  ucsResolvidas: number | null;
  faturaBoletoId: number | null;
  linhaDigitavel: string | null;
  emitidoEm: string | null;
  nfNumero: string | null;
  nfAnexoPath: string | null;
  maloteEntregueEm: string | null;
  situacaoPagamento: SituacaoPagamento | null;
  valorRecebido: number | null;
  auditadoEm: string | null;
  /** Estado de prazo da régua (#5787), já calculado para esta competência. */
  estadoPrazo: EstadoPrazoEmissao | null;
  /** Data-limite do malote pela régua, "YYYY-MM-DD". */
  limiteMalote: string | null;
  /** Data da auditoria pela régua, "YYYY-MM-DD". */
  dataAuditoria: string | null;
}

const soData = (iso: string | null | undefined) => String(iso ?? '').slice(0, 10);

/**
 * Pendências da Secretaria no mês, da mais grave para a menos.
 *
 * O que é impedimento: mês não emitido, UC sem resolver, boleto sem linha digitável e pagamento
 * abaixo do esperado — nenhum deles se resolve com o tempo, e todos mudam o que a Prefeitura recebe
 * ou paga. O que é atenção: emissão fora do prazo (já aconteceu, só precisa estar registrada), NF e
 * malote ainda no prazo. Malote vencido sobe para impedimento: é dele que corre o prazo do edital.
 */
export function pendenciasDaSecretaria(f: FechamentoAuditoria, hojeIso: string): Pendencia[] {
  const p: Pendencia[] = [];
  const hoje = soData(hojeIso);
  const zerado = Number(f.totalVigor ?? 0) <= 0;

  if (f.status !== 'emitido') {
    p.push({
      codigo: 'nao_emitida',
      texto: f.status === 'aberto' ? 'Mês ainda aberto: feche e emita.' : 'Mês fechado e não emitido.',
      gravidade: 'impedimento',
    });
  }

  const faltando = Math.max(Number(f.ucsNoGrupo ?? 0) - Number(f.ucsResolvidas ?? 0), 0);
  if (faltando > 0) {
    p.push({
      codigo: 'ucs_fora',
      texto: `${faltando} UC(s) ficaram fora do fechamento.`,
      gravidade: 'impedimento',
    });
  }

  // Mês zerado é emitido sem título de propósito; cobrar linha digitável aí seria alarme falso.
  if (f.status === 'emitido' && !zerado && !String(f.linhaDigitavel ?? '').trim()) {
    p.push({
      codigo: 'boleto_sem_linha',
      texto: 'Boleto sem linha digitável: o banco não confirmou o registro.',
      gravidade: 'impedimento',
    });
  }

  if (f.situacaoPagamento === 'pago_a_menor') {
    p.push({
      codigo: 'pago_a_menor',
      texto: 'Pagamento abaixo do esperado, além da retenção prevista.',
      gravidade: 'impedimento',
    });
  }

  if (f.status === 'emitido' && f.estadoPrazo === 'emitido_fora_do_prazo') {
    p.push({
      codigo: 'emitida_fora_do_prazo',
      texto: 'Emitida depois do limite do contrato.',
      gravidade: 'atencao',
    });
  }

  if (f.status === 'emitido' && !String(f.nfNumero ?? '').trim()) {
    p.push({ codigo: 'sem_nf', texto: 'Nota fiscal não registrada.', gravidade: 'atencao' });
  }

  if (f.status === 'emitido' && !f.maloteEntregueEm) {
    const venceu = !!f.limiteMalote && hoje > soData(f.limiteMalote);
    p.push({
      codigo: venceu ? 'malote_atrasado' : 'sem_malote',
      texto: venceu
        ? 'Malote não entregue e o prazo do contrato já passou.'
        : 'Malote ainda não entregue.',
      // O prazo de 30 dias do edital conta da entrega: atrasar aqui atrasa o pagamento.
      gravidade: venceu ? 'impedimento' : 'atencao',
    });
  }

  return p;
}

export interface LinhaAuditoria extends FechamentoAuditoria {
  pendencias: Pendencia[];
  impedimentos: number;
  /** Pode registrar auditoria: só mês emitido. */
  podeAuditar: boolean;
  /** Frase do motivo quando não pode, para a tela não deixar o botão cinza sem explicação. */
  motivoNaoAuditar: string | null;
}

export function montarLinhaAuditoria(f: FechamentoAuditoria, hojeIso: string): LinhaAuditoria {
  const pendencias = pendenciasDaSecretaria(f, hojeIso);
  const emitida = f.status === 'emitido';
  return {
    ...f,
    pendencias,
    impedimentos: pendencias.filter(x => x.gravidade === 'impedimento').length,
    podeAuditar: emitida,
    motivoNaoAuditar: emitida ? null : 'Só o mês emitido pode ser auditado: emita antes.',
  };
}

export interface ResumoCiclo {
  secretarias: number;
  emitidas: number;
  auditadas: number;
  comImpedimento: number;
  /** Todas as Secretarias auditadas — é o que significa "ciclo auditado". */
  cicloAuditado: boolean;
  /** Dia da auditoria pela régua, quando todas as Secretarias concordam. */
  dataAuditoria: string | null;
  /** Vence hoje ou já passou e ainda falta auditar. */
  atrasado: boolean;
}

/** Leitura do ciclo a partir das linhas. Nada é guardado: o resumo é sempre derivado. */
export function resumirCiclo(linhas: LinhaAuditoria[], hojeIso: string): ResumoCiclo {
  const datas = [...new Set(linhas.map(l => soData(l.dataAuditoria)).filter(Boolean))];
  const auditadas = linhas.filter(l => !!l.auditadoEm).length;
  const dataAuditoria = datas.length === 1 ? datas[0] : null;
  const faltam = linhas.length - auditadas;
  return {
    secretarias: linhas.length,
    emitidas: linhas.filter(l => l.status === 'emitido').length,
    auditadas,
    comImpedimento: linhas.filter(l => l.impedimentos > 0).length,
    cicloAuditado: linhas.length > 0 && auditadas === linhas.length,
    dataAuditoria,
    atrasado: faltam > 0 && !!dataAuditoria && soData(hojeIso) >= dataAuditoria,
  };
}

/** Itens que a auditoria carimba, na ordem em que a tela os mostra. */
export const ITENS_AUDITORIA = [
  { codigo: 'emissao', texto: 'Mês emitido e dentro do que o contrato pede' },
  { codigo: 'relacao', texto: 'Relação das UCs conferida, sem unidade fora do fechamento' },
  { codigo: 'valor', texto: 'Valor do boleto conferido contra o fechamento' },
  { codigo: 'nf', texto: 'Nota fiscal emitida e registrada' },
  { codigo: 'malote', texto: 'Malote entregue, ou com data prevista' },
] as const;

export type CodigoItemAuditoria = typeof ITENS_AUDITORIA[number]['codigo'];

/** O que vai em `auditoria_itens`: só o que foi marcado, com a data do carimbo. */
export function montarItensAuditoria(marcados: Iterable<string>): Record<string, boolean> {
  const set = new Set(marcados);
  return Object.fromEntries(ITENS_AUDITORIA.map(i => [i.codigo, set.has(i.codigo)]));
}
